import type { PromoCampaign } from "./types";

/**
 * The arithmetic behind the promotions page, kept out of the component.
 *
 * Not for tidiness: these are the figures an operator makes a money decision
 * from, and a figure computed inside JSX is a figure with no test. The exposure
 * calculation in particular is the one thing on that page that could be
 * dangerously wrong — understating it would tell somebody a campaign is cheaper
 * than it is.
 */

export type CampaignState = "live" | "scheduled" | "ended" | "fully-claimed" | "inactive";

/**
 * Where a campaign is in its life.
 *
 * Five states rather than the `active` boolean, because that flag alone is
 * misleading in three directions: an active campaign whose end date has passed
 * accepts nothing, one whose start date has not arrived accepts nothing yet, and
 * one at its quota accepts nothing ever again. All three read as live if the flag
 * is all you show — and "live" is the state that costs money unattended.
 *
 * Order of checks matters. `inactive` wins over everything, because an operator
 * who switched a campaign off does not care that it would also have ended; and
 * `ended` beats `fully-claimed` because the offer closing is the plainer reason.
 */
export function campaignState(campaign: PromoCampaign, now = Date.now()): CampaignState {
  if (!campaign.active) return "inactive";
  if (campaign.endsAt && new Date(campaign.endsAt).getTime() <= now) return "ended";
  if (campaign.startsAt && new Date(campaign.startsAt).getTime() > now) return "scheduled";
  if (campaign.maxRedemptions !== null && campaign.redemptions >= campaign.maxRedemptions) {
    return "fully-claimed";
  }
  return "live";
}

/** Whether a campaign can still hand out money right now. */
export function isRedeemable(campaign: PromoCampaign, now = Date.now()): boolean {
  return campaignState(campaign, now) === "live";
}

/**
 * Whether deleting this campaign would be allowed.
 *
 * The API refuses once anybody has redeemed, because those rows are what explain
 * the promotional credit sitting on customers' ledgers — deleting the campaign
 * would leave the money with nothing naming where it came from. Deactivation is
 * the answer for a campaign that has run.
 *
 * Mirrored here so the console can offer the action only where it will succeed.
 * A Delete button that is always refused is one that teaches an operator to
 * click through errors, which is a habit worth more than the button. The API
 * check remains the real one: this decides what to show, not what is permitted,
 * and the count it reads is as fresh as the last list fetch.
 *
 * Deliberately not "and it is inactive". A draft written by mistake is the case
 * this exists for, and requiring somebody to turn a campaign off before deleting
 * a campaign that was never on is a step that teaches nothing.
 */
export function isDeletable(campaign: PromoCampaign): boolean {
  return campaign.redemptions === 0;
}

/**
 * The most this campaign can still cost, in micros.
 *
 * The number the console exists to show and the one a campaign row cannot state
 * on its own: `$300` and `1,000` are two innocuous fields whose product is a
 * $300,000 commitment. An operator reading a list of campaigns has no way to see
 * that without multiplying by hand, per row, which is exactly the arithmetic
 * nobody does before clicking Activate.
 *
 * `null` means unbounded — a campaign with no redemption quota can cost whatever
 * the world redeems. Returned as null rather than as a very large number so the
 * page has to say "unbounded" rather than printing a figure that looks like a
 * limit.
 *
 * A **grant** exposes its amount for every remaining slot. A **match** exposes
 * its cap for every remaining slot *plus* every claim already made and not yet
 * paid, because those are committed: somebody holds that offer and will collect
 * it on their next payment. Leaving the pending ones out is the understatement
 * that matters, since a match campaign's whole shape is claims now and money
 * later.
 *
 * Only counted for a campaign that can still be redeemed. An ended or
 * deactivated one exposes only its pending matches — the offer is closed, but a
 * promise already made is still a promise.
 */
export function remainingExposureMicros(
  campaign: PromoCampaign,
  now = Date.now(),
): number | null {
  const perRedemption =
    campaign.kind === "grant" ? (campaign.grantMicros ?? 0) : (campaign.matchCapMicros ?? 0);

  // Claims already made that will be paid on a future top-up. Committed
  // regardless of whether the offer is still open.
  const committed = campaign.kind === "match" ? campaign.pendingCount * perRedemption : 0;

  if (!isRedeemable(campaign, now)) return committed;
  if (campaign.maxRedemptions === null) return null;

  const slots = Math.max(0, campaign.maxRedemptions - campaign.redemptions);
  return committed + slots * perRedemption;
}

export interface ExposureTotal {
  micros: number;
  /** True when any live campaign has no redemption quota. */
  unbounded: boolean;
}

/**
 * Total remaining exposure across every campaign.
 *
 * `unbounded` is carried alongside the sum rather than collapsing it to
 * Infinity, because the page has to say both things at once: "at least this
 * much, and one campaign has no ceiling". A single figure would have to choose
 * between understating and being meaningless.
 */
export function totalExposure(campaigns: PromoCampaign[], now = Date.now()): ExposureTotal {
  let micros = 0;
  let unbounded = false;

  for (const campaign of campaigns) {
    const exposure = remainingExposureMicros(campaign, now);
    if (exposure === null) {
      unbounded = true;
      continue;
    }
    micros += exposure;
  }

  return { micros, unbounded };
}

/**
 * What fraction of the quota is gone, as 0–1, or null where there is no quota.
 *
 * Drives the progress bar, which exists so a campaign about to run out is visible
 * by scanning rather than by comparing two numbers per row.
 */
export function quotaUsed(campaign: PromoCampaign): number | null {
  if (campaign.maxRedemptions === null || campaign.maxRedemptions === 0) return null;
  return Math.min(1, campaign.redemptions / campaign.maxRedemptions);
}

/**
 * How many redeemers went on to pay, as 0–1, or null with nothing to divide.
 *
 * Null rather than zero on an unredeemed campaign: 0% conversion reads as a
 * failed offer, and "nobody has tried it yet" is a different statement.
 */
export function conversionRate(campaign: PromoCampaign): number | null {
  if (campaign.redemptions === 0) return null;
  return campaign.convertedCount / campaign.redemptions;
}

/**
 * The offer as a sentence, in the terms it was configured in.
 *
 * Shared by the list and by the form's live preview, so what an operator reads
 * back while typing is exactly what the list will say afterwards. Two
 * descriptions of one offer is how a form comes to promise something the list
 * contradicts.
 */
export function describeOffer(
  campaign: Pick<
    PromoCampaign,
    "kind" | "grantMicros" | "matchBps" | "matchCapMicros" | "matchMinTopUpMicros" | "creditTtlDays"
  >,
  money: (micros: number) => string,
): string {
  const life =
    campaign.creditTtlDays === null || campaign.creditTtlDays === undefined
      ? "never expires"
      : `expires ${campaign.creditTtlDays} days after redemption`;

  if (campaign.kind === "grant") {
    return `${money(campaign.grantMicros ?? 0)} credit, ${life}`;
  }

  const rate = (campaign.matchBps ?? 0) / 100;
  const floor =
    campaign.matchMinTopUpMicros === null || campaign.matchMinTopUpMicros === undefined
      ? ""
      : ` on top-ups of ${money(campaign.matchMinTopUpMicros)} or more`;

  return `+${rate}%${floor}, up to ${money(campaign.matchCapMicros ?? 0)}, ${life}`;
}
